import { EventEmitter, inject, Injectable } from '@angular/core';
import {
  Address,
  CheckoutValidator,
  ValidationErrorKeys,
  ValidationErrors,
  WrongCityWarning,
} from '@checkout-v2/const';
import { DeliveryAddressFacade } from '@checkout-v2/data-access';
import { BusinessRegionFacade } from '@city/data-access';
import { CityService } from '@city/feature';
import { StateUtils } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DeliveryAddressService extends CheckoutValidator {
  private readonly deliveryAddressFacade = inject(DeliveryAddressFacade);
  private readonly cityService = inject(CityService);
  private readonly businessRegionFacade = inject(BusinessRegionFacade);

  addressList$ = this.deliveryAddressFacade.getAddressList();
  activeAddressId$ = this.deliveryAddressFacade.getActiveAddressId();
  activeAddress$ = this.deliveryAddressFacade.getActiveAddress();
  hasActiveAddress$ = this.activeAddress$.pipe<boolean>(map(Boolean));
  loading$ = this.deliveryAddressFacade.getLoading();
  success$ = this.deliveryAddressFacade.getSuccess();
  editableAddressId$ = new BehaviorSubject<string | undefined>(undefined);

  // Может так случится, что город в указанном в доставке адресе не совпадет
  // с городом в сторе. Бизнесс от этого ломается.
  // Поэтому пытаемся найти нессответсвие, сообщить и помочь пользователю с городами.
  addressCityMatches$: Observable<WrongCityWarning[]> = combineLatest([
    this.activeAddress$.pipe(
      distinctUntilChanged((prev, next) => {
        if (next && prev) {
          return prev.id === next.id;
        }

        return false;
      }),
      tap((address) => {
        if (address && address.point) {
          this.businessRegionFacade.loadBusinessRegionForPoint(address.point);
        }
      }),
    ),
    this.cityService.activeBaseStore$,
    this.businessRegionFacade.getBusinessRegionForPoint(),
  ]).pipe(
    map(([address, baseStore, businessRegion]) => {
      if (!address || !businessRegion || !baseStore) {
        return [];
      }

      if (businessRegion.clientPoint.slug !== baseStore.code) {
        return [{ name: address.town || address.district, point: address.point }];
      }

      return [];
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  floorNotSpecified$ = this.activeAddress$.pipe(
    map((address) => (address ? !address.privateHouse && !address.floor : false)),
  );

  // эмитим во время сохранения адреса
  savedAddressEvent = new EventEmitter();
  selectActiveAddressIdEvent = new EventEmitter<string | undefined>();

  getAddress(id: string): Observable<Address | undefined> {
    return this.deliveryAddressFacade.getAddress(id);
  }

  closeAddressModal(): void {
    this.savedAddressEvent.emit();
  }

  selectActiveAddressId(id: string | undefined): void {
    this.deliveryAddressFacade.selectActiveAddressId(id);
    this.selectActiveAddressIdEvent.emit(id);
  }

  saveAddress(address: Address): void {
    this.deliveryAddressFacade.saveAddress(address);
  }

  editSavedAddress(address: Address): void {
    this.deliveryAddressFacade.editSavedAddress(address);
  }

  deleteSavedAddress(id: string): void {
    this.deliveryAddressFacade.deleteSavedAddress(id);
  }

  getAddressListState(): Observable<StateUtils.LoaderState<Address[]>> {
    return this.deliveryAddressFacade.getAddressListState();
  }

  isEmptyOrHasError(): Observable<boolean> {
    return this.getAddressListState().pipe(map((state) => !!state.error));
  }

  getValidationErrors(): Observable<ValidationErrors> {
    return combineLatest({
      isEmptyOrHasError: this.isEmptyOrHasError(),
      activeAddressId: this.activeAddressId$,
      citiesMatch: this.addressCityMatches$,
    }).pipe(
      map(({ isEmptyOrHasError, activeAddressId, citiesMatch }) => {
        const validationErrors: ValidationErrors = {};

        if (!isEmptyOrHasError && !activeAddressId) {
          validationErrors[ValidationErrorKeys.DELIVERY_ADDRESS_REQUIRED] = 'выберите адрес доставки';
        }

        if (citiesMatch.length > 0) {
          validationErrors[ValidationErrorKeys.DELIVERY_CITY_MISMATCH_WITH_CURRENT_CITY] =
            'город в адресе доставки не соответствует текущему городу';
        }

        return validationErrors;
      }),
    );
  }
}
